import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/db";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Users, Shield, Server, Plus, Trash2, AlertCircle, Link2, Building, Upload, X, BookOpen, Send, MailCheck, Key, RefreshCw, Mail, RotateCcw } from "lucide-react";
import { itemStateBadge, paymentStateBadge } from "@/routes/orders.$id";
import { cashOutBadge } from "@/routes/orders";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TiptapEditor } from "@/components/TiptapEditor";

export const Route = createFileRoute("/admin")({ component: () => <Guard module="admin" level="admin"><AdminPage /></Guard> });

const MODULES = [
  { key: "dashboard", label: "Tableau de bord" },
  { key: "members", label: "Adhérents" },
  { key: "treasury", label: "Trésorerie" },
  { key: "mailing", label: "Mails" },
  { key: "documents", label: "Documents" },
  { key: "admin", label: "Administration" },
] as const;

const LEVELS = [
  { v: "none", label: "Aucun" },
  { v: "read", label: "Lecture" },
  { v: "write", label: "Écriture" },
  { v: "admin", label: "Admin" },
] as const;

function AdminPage() {
  return (
    <>
      <PageHeader title="Administration" description="Utilisateurs, rôles et paramètres techniques" />
      <Tabs defaultValue="organization">
        <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1">
          <TabsTrigger value="organization"><Building className="h-4 w-4 mr-1.5" />Association</TabsTrigger>
          <TabsTrigger value="users"><Users className="h-4 w-4 mr-1.5" />Utilisateurs</TabsTrigger>
          <TabsTrigger value="roles"><Shield className="h-4 w-4 mr-1.5" />Rôles & permissions</TabsTrigger>
          <TabsTrigger value="smtp"><Server className="h-4 w-4 mr-1.5" />Paramètres SMTP</TabsTrigger>
          <TabsTrigger value="helloasso"><Link2 className="h-4 w-4 mr-1.5" />HelloAsso</TabsTrigger>
          <TabsTrigger value="helloasso-docs"><BookOpen className="h-4 w-4 mr-1.5" />Documentation HelloAsso</TabsTrigger>
          <TabsTrigger value="system-emails"><Mail className="h-4 w-4 mr-1.5" />Emails système</TabsTrigger>
        </TabsList>
        <TabsContent value="organization" className="mt-4"><OrganizationTab /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="roles" className="mt-4"><RolesTab /></TabsContent>
        <TabsContent value="smtp" className="mt-4"><SmtpTab /></TabsContent>
        <TabsContent value="helloasso" className="mt-4"><HelloAssoTab /></TabsContent>
        <TabsContent value="helloasso-docs" className="mt-4"><HelloAssoDocsTab /></TabsContent>
        <TabsContent value="system-emails" className="mt-4"><SystemEmailsTab /></TabsContent>
      </Tabs>
    </>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const { data: profiles = [] } = useQuery({
    queryKey: ["profiles"],
    queryFn: async () => (await supabase.from("profiles").select("*").order("email")).data ?? [],
  });
  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await supabase.from("roles").select("*").order("name")).data ?? [],
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ["user_roles"],
    queryFn: async () => (await supabase.from("user_roles").select("*")).data ?? [],
  });

  const setRole = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string | null }) => {
      await supabase.from("user_roles").delete().eq("user_id", userId);
      if (roleId) {
        const { error } = await supabase.from("user_roles").insert({ user_id: userId, role_id: roleId });
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["user_roles"] }); toast.success("Rôle mis à jour"); },
    onError: (e: any) => toast.error(e.message),
  });

  const generateTempPassword = () => {
    // 12 caractères : lettres min, maj, chiffres, ponctuation simple. Sans caractères ambigus.
    const charset = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789@#$%&*";
    let out = "";
    const rnd = new Uint32Array(12);
    crypto.getRandomValues(rnd);
    for (let i = 0; i < 12; i++) out += charset[rnd[i] % charset.length];
    return out;
  };

  const [createOpen, setCreateOpen] = useState(false);
  const makeEmpty = () => ({ email: "", password: generateTempPassword(), first_name: "", last_name: "", role_id: "none", send_invite: true });
  const [newUser, setNewUser] = useState(makeEmpty);

  const openCreate = () => { setNewUser(makeEmpty()); setCreateOpen(true); };

  const createUser = useMutation({
    mutationFn: async () => {
      const body: any = {
        email: newUser.email.trim(),
        password: newUser.password,
        send_invite: newUser.send_invite,
        reset_url_base: window.location.origin,
      };
      if (newUser.first_name) body.first_name = newUser.first_name;
      if (newUser.last_name) body.last_name = newUser.last_name;
      if (newUser.role_id && newUser.role_id !== "none") body.role_ids = [newUser.role_id];
      return await api.post<{ invitation_sent?: boolean; invitation_error?: string | null }>("/api/profiles", body, { rawJson: true });
    },
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["profiles"] });
      qc.invalidateQueries({ queryKey: ["user_roles"] });
      if (newUser.send_invite) {
        if (res?.invitation_sent) toast.success("Utilisateur créé — email d'activation envoyé");
        else toast.warning(`Utilisateur créé, mais l'email n'a pas pu être envoyé${res?.invitation_error ? ` (${res.invitation_error})` : ""}`);
      } else {
        toast.success("Utilisateur créé");
      }
      setCreateOpen(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Erreur lors de la création"),
  });

  return (
    <>
      <Card className="p-4 mb-4 border-primary/30 bg-primary/5">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Inviter un membre du bureau :</strong> communiquez-lui l'URL de l'application pour qu'il crée son
            compte via l'écran de connexion, ou créez-le directement avec le bouton ci-dessous. Vous lui attribuerez
            ensuite son rôle dans le tableau.
          </div>
        </div>
      </Card>
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Nouvel utilisateur</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Statut</TableHead><TableHead>Rôle attribué</TableHead></TableRow></TableHeader>
          <TableBody>
            {profiles.map((p: any) => {
              const ur: any = assignments.find((a: any) => a.user_id === p.id);
              return (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.email}</TableCell>
                  <TableCell>{p.is_super_admin ? <Badge className="bg-accent text-accent-foreground">Super-admin</Badge> : <Badge variant="outline">Membre</Badge>}</TableCell>
                  <TableCell>
                    <Select
                      value={ur?.role_id ?? "none"}
                      onValueChange={(v) => setRole.mutate({ userId: p.id, roleId: v === "none" ? null : v })}
                      disabled={p.is_super_admin}
                    >
                      <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Aucun rôle —</SelectItem>
                        {roles.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Prénom</Label>
                <Input value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Nom</Label>
                <Input value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Mot de passe temporaire * <span className="text-xs text-muted-foreground">(min. 8 caractères)</span></Label>
              <div className="flex gap-2">
                <Input type="text" className="font-mono" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
                <Button type="button" variant="outline" size="icon" title="Régénérer" onClick={() => setNewUser({ ...newUser, password: generateTempPassword() })}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Mot de passe généré automatiquement. L'utilisateur le changera via le lien d'activation envoyé par email.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Rôle</Label>
              <Select value={newUser.role_id} onValueChange={(v) => setNewUser({ ...newUser, role_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun rôle —</SelectItem>
                  {roles.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm">Envoyer un email d'activation</Label>
                <p className="text-xs text-muted-foreground">Via le profil SMTP « Intranet » — lien sécurisé par token (24 h).</p>
              </div>
              <Switch checked={newUser.send_invite} onCheckedChange={(v) => setNewUser({ ...newUser, send_invite: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button
              onClick={() => createUser.mutate()}
              disabled={!newUser.email || newUser.password.length < 8 || createUser.isPending}
            >
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RolesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await supabase.from("roles").select("*").order("name")).data ?? [],
  });
  const { data: perms = [] } = useQuery({
    queryKey: ["role_permissions"],
    queryFn: async () => (await supabase.from("role_permissions").select("*")).data ?? [],
  });

  const addRole = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.from("roles").insert({ name, description: desc || null }).select().single();
      if (error) throw error;
      // Initialiser permissions à 'none' pour chaque module
      const inserts = MODULES.map((m) => ({ role_id: data.id, module: m.key, level: "none" as const }));
      await supabase.from("role_permissions").insert(inserts);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roles"] });
      qc.invalidateQueries({ queryKey: ["role_permissions"] });
      toast.success("Rôle créé"); setOpen(false); setName(""); setDesc("");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updatePerm = useMutation({
    mutationFn: async ({ roleId, module, level }: { roleId: string; module: string; level: string }) => {
      const existing: any = perms.find((p: any) => p.role_id === roleId && p.module === module);
      if (existing) {
        const { error } = await supabase.from("role_permissions").update({ level: level as any }).eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("role_permissions").insert({ role_id: roleId, module: module as any, level: level as any });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["role_permissions"] }),
    onError: (e: any) => toast.error(e.message),
  });

  const delRole = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("roles").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["roles"] }); toast.success("Rôle supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-2" />Nouveau rôle</Button>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rôle</TableHead>
              {MODULES.map((m) => <TableHead key={m.key} className="text-center">{m.label}</TableHead>)}
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell>
                  <div className="font-medium">{r.name}{r.is_system && <Badge variant="outline" className="ml-2 text-xs">système</Badge>}</div>
                  {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                </TableCell>
                {MODULES.map((m) => {
                  const p: any = perms.find((x: any) => x.role_id === r.id && x.module === m.key);
                  return (
                    <TableCell key={m.key} className="text-center">
                      <Select value={p?.level ?? "none"} onValueChange={(v) => updatePerm.mutate({ roleId: r.id, module: m.key, level: v })}>
                        <SelectTrigger className="w-[100px] mx-auto h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  );
                })}
                <TableCell>
                  {!r.is_system && (
                    <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Supprimer le rôle "${r.name}" ?`)) delRole.mutate(r.id); }}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau rôle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Vice-président" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
            <p className="text-xs text-muted-foreground">Les permissions par module seront initialisées à « Aucun ». Vous pourrez les ajuster ensuite.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => addRole.mutate()} disabled={!name}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type SmtpProfile = {
  id?: string;
  role_name: string;
  from_name: string | null;
  from_email: string | null;
  host: string | null;
  port: number | null;
  username: string | null;
  password?: string | null;
  use_tls: boolean | null;
  encryption?: 'none' | 'starttls' | 'ssl' | null;
  is_default: boolean;
  sort_order?: number | null;
};

function SmtpTab() {
  const qc = useQueryClient();
  const { data: profiles = [] } = useQuery({
    queryKey: ["smtp_profiles"],
    queryFn: async () => await api.get<any[]>('/api/smtp-profiles'),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SmtpProfile | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testProfileId, setTestProfileId] = useState<string | null>(null);
  const [testProfileName, setTestProfileName] = useState("");

  const openNew = () =>
    setEditing({
      role_name: "",
      from_name: "",
      from_email: "",
      host: "",
      port: 587,
      username: "",
      password: "",
      use_tls: true,
      encryption: 'starttls',
      is_default: profiles.length === 0,
      sort_order: profiles.length,
    });

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const payload: any = {
        role_name: editing.role_name,
        from_name: editing.from_name || null,
        from_email: editing.from_email || null,
        host: editing.host || null,
        port: editing.port ? Number(editing.port) : 587,
        username: editing.username || null,
        use_tls: !!editing.use_tls,
        encryption: editing.encryption ?? (editing.use_tls ? 'starttls' : 'none'),
        is_default: !!editing.is_default,
        sort_order: editing.sort_order ?? 0,
      };
      // n'envoyer le mot de passe que s'il a été modifié (non vide)
      if (editing.password && editing.password.length > 0) payload.password = editing.password;

      if (editing.id) {
        const { error } = await supabase.from("smtp_profiles").update(payload).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("smtp_profiles").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smtp_profiles"] });
      toast.success("Profil enregistré");
      setOpen(false);
      setEditing(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("smtp_profiles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smtp_profiles"] });
      toast.success("Profil supprimé");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setDefault = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("smtp_profiles").update({ is_default: true }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smtp_profiles"] }),
  });

  const testSmtp = useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => {
      const res = await api.post<{ success: boolean; message: string }>(`/api/smtp-profiles/${id}/test`, { test_email: email });
      return res;
    },
    onSuccess: (data: any) => {
      toast.success(data?.message || "Email de test envoyé");
      setTestOpen(false);
      setTestEmail("");
      setTestProfileId(null);
    },
    onError: (e: any) => toast.error(e.message || "Échec du test SMTP"),
  });

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-warning/10 border-warning/30 text-sm flex gap-3">
        <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
        <div>
          Configurez une adresse d'envoi par rôle du bureau (Président, Trésorier, …). Le profil marqué
          <strong> par défaut </strong> est utilisé si aucun expéditeur n'est choisi lors de l'envoi.
          L'environnement Lovable Cloud ne peut pas se connecter à un SMTP classique : l'envoi réel se fera
          depuis votre hébergement.
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => { openNew(); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Nouveau profil
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rôle</TableHead>
              <TableHead>Expéditeur</TableHead>
              <TableHead>Serveur</TableHead>
              <TableHead>Mot de passe</TableHead>
              <TableHead>Défaut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {profiles.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Aucun profil SMTP. Créez-en un pour commencer.
                </TableCell>
              </TableRow>
            )}
            {profiles.map((p: any) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.role_name}</TableCell>
                <TableCell className="text-sm">
                  {p.from_name ? <div>{p.from_name}</div> : null}
                  <div className="text-muted-foreground">{p.from_email ?? "—"}</div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {p.host ? `${p.host}:${p.port ?? 587}` : "—"}
                </TableCell>
                <TableCell>
                  {p.has_password ? (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                      <Key className="h-3 w-3 mr-1" />Renseigné
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-destructive border-destructive/20 bg-destructive/5">
                      Absent
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {p.is_default ? (
                    <Badge>Par défaut</Badge>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setDefault.mutate(p.id)}>
                      Définir
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTestProfileId(p.id);
                      setTestProfileName(p.role_name);
                      setTestEmail("");
                      setTestOpen(true);
                    }}
                  >
                    <MailCheck className="h-4 w-4 mr-1" />Tester
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing({ ...p, password: "" });
                      setOpen(true);
                    }}
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Supprimer le profil "${p.role_name}" ?`)) del.mutate(p.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le profil SMTP" : "Nouveau profil SMTP"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Nom du rôle *</Label>
                <Input
                  value={editing.role_name}
                  onChange={(e) => setEditing({ ...editing, role_name: e.target.value })}
                  placeholder="Président, Trésorier, Secrétaire…"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nom expéditeur</Label>
                <Input
                  value={editing.from_name ?? ""}
                  onChange={(e) => setEditing({ ...editing, from_name: e.target.value })}
                  placeholder="Mon Asso – Trésorier"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Adresse expéditeur *</Label>
                <Input
                  type="email"
                  value={editing.from_email ?? ""}
                  onChange={(e) => setEditing({ ...editing, from_email: e.target.value })}
                  placeholder="tresorier@monasso.fr"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Serveur SMTP (host)</Label>
                <Input
                  value={editing.host ?? ""}
                  onChange={(e) => setEditing({ ...editing, host: e.target.value })}
                  placeholder="smtp.monasso.fr"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={editing.port ?? 587}
                  onChange={(e) => setEditing({ ...editing, port: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Chiffrement</Label>
                <Select
                  value={editing.encryption ?? (editing.use_tls ? 'starttls' : 'none')}
                  onValueChange={(v) => setEditing({ ...editing, encryption: v as any, use_tls: v !== 'none' })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
                    <SelectItem value="ssl">SSL/TLS (port 465)</SelectItem>
                    <SelectItem value="none">Aucun (non chiffré)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Identifiant</Label>
                <Input
                  value={editing.username ?? ""}
                  onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Mot de passe {editing.id && <span className="text-xs text-muted-foreground">(laisser vide pour conserver)</span>}</Label>
                <Input
                  type="password"
                  value={editing.password ?? ""}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Switch
                  checked={!!editing.is_default}
                  onCheckedChange={(v) => setEditing({ ...editing, is_default: v })}
                />
                <Label>Profil par défaut</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              onClick={() => save.mutate()}
              disabled={!editing?.role_name || !editing?.from_email || save.isPending}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tester le profil SMTP</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Un email de test sera envoyé via le profil <strong>{testProfileName}</strong>.
            </p>
            <div className="space-y-1.5">
              <Label>Adresse email de test</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="votre-email@exemple.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Annuler</Button>
            <Button
              onClick={() => {
                if (!testProfileId || !testEmail) return;
                testSmtp.mutate({ id: testProfileId, email: testEmail });
              }}
              disabled={!testEmail || !testProfileId || testSmtp.isPending}
            >
              {testSmtp.isPending ? "Envoi…" : "Envoyer le test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HelloAssoTab() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["helloasso_settings"],
    queryFn: async () => (await supabase.from("helloasso_settings").select("*").limit(1).maybeSingle()).data,
  });
  const { data: log = [] } = useQuery({
    queryKey: ["helloasso_sync_log"],
    queryFn: async () => (await supabase.from("helloasso_sync_log").select("*").order("started_at", { ascending: false }).limit(10)).data ?? [],
  });

  const [form, setForm] = useState<any>(null);
  const current = form ?? settings ?? { client_id: "", client_secret: "", organization_slug: "" };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        client_id: current.client_id, client_secret: current.client_secret,
        organization_slug: current.organization_slug, updated_at: new Date().toISOString(),
      };
      if (settings?.id) {
        const { error } = await supabase.from("helloasso_settings").update(payload).eq("id", settings.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("helloasso_settings").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["helloasso_settings"] }); toast.success("Identifiants enregistrés"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="p-4 border-primary/30 bg-primary/5">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <div><strong>Où trouver ces informations ?</strong></div>
            <div>Sur HelloAsso : <em>Mon compte → Intégrations → API</em>. Générez une paire <code>client_id</code> / <code>client_secret</code>.</div>
            <div>Le <strong>nom court de l'organisation</strong> est visible dans l'URL de votre page : <code>helloasso.com/associations/<u>mon-asso</u></code>.</div>
          </div>
        </div>
      </Card>
      <Card className="p-6">
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="grid grid-cols-1 gap-4">
          {/* Champs leurres pour empêcher l'auto-remplissage navigateur */}
          <input type="text" name="fakeusernameremembered" autoComplete="username" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} />
          <input type="password" name="fakepasswordremembered" autoComplete="new-password" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} />
          <div className="space-y-1.5"><Label>Nom court de l'organisation</Label><Input autoComplete="off" name="ha-org-slug" value={current.organization_slug ?? ""} onChange={(e) => setForm({ ...current, organization_slug: e.target.value })} placeholder="mon-asso" /></div>
          <div className="space-y-1.5"><Label>Client ID</Label><Input autoComplete="off" name="ha-client-id" value={current.client_id ?? ""} onChange={(e) => setForm({ ...current, client_id: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label>Client Secret</Label>
            <Input
              type="password"
              autoComplete="new-password"
              name="ha-client-secret"
              value={current.client_secret ?? ""}
              onChange={(e) => setForm({ ...current, client_secret: e.target.value })}
              placeholder="Insérez le nouveau code client secret HelloAsso pour mettre à jour la base"
            />
            <p className="text-xs text-muted-foreground">Pour des raisons de sécurité, le secret enregistré n'est jamais réaffiché. Laissez vide pour le conserver, ou saisissez-en un nouveau pour le remplacer.</p>
          </div>
        </form>
        <Button className="mt-6" onClick={() => save.mutate()} disabled={save.isPending || !current.client_id || !current.organization_slug}>
          Enregistrer
        </Button>
        {settings?.last_sync_at && (
          <p className="text-xs text-muted-foreground mt-3">Dernière synchronisation : {new Date(settings.last_sync_at).toLocaleString("fr-FR")}</p>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold mb-3">Historique des synchronisations</h3>
        {log.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune synchronisation effectuée.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Statut</TableHead>
              <TableHead className="text-right">Adhérents</TableHead>
              <TableHead className="text-right">Cotis.</TableHead>
              <TableHead className="text-right">Opér.</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {log.map((l: any) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs">{new Date(l.started_at).toLocaleString("fr-FR")}</TableCell>
                  <TableCell>
                    {l.status === "success" ? <Badge className="bg-success text-success-foreground">OK</Badge>
                      : l.status === "error" ? <Badge variant="destructive" title={l.error_message ?? ""}>Erreur</Badge>
                      : <Badge variant="outline">En cours</Badge>}
                  </TableCell>
                  <TableCell className="text-right text-xs">+{l.members_created} / ↻{l.members_updated}</TableCell>
                  <TableCell className="text-right text-xs">{l.memberships_created}</TableCell>
                  <TableCell className="text-right text-xs">{l.transactions_created}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function OrganizationTab() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["org_settings"],
    queryFn: async () => (await supabase.from("org_settings").select("*").limit(1).maybeSingle()).data,
  });

  const [name, setName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const currentName = name ?? settings?.name ?? "";
  const logoUrl = settings?.logo_url ?? null;

  const save = useMutation({
    mutationFn: async (patch: { name?: string | null; logo_url?: string | null }) => {
      if (!settings?.id) {
        const { error } = await supabase.from("org_settings").insert({ ...patch, updated_at: new Date().toISOString() });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_settings").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", settings.id);
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org_settings"] }); toast.success("Enregistré"); },
    onError: (e: any) => toast.error(e.message),
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Veuillez sélectionner une image"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop volumineuse (max 2 Mo)"); return; }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("org-assets").upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("org-assets").getPublicUrl(path);
      await save.mutateAsync({ logo_url: data.publicUrl });
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeLogo = () => save.mutate({ logo_url: null });

  return (
    <Card className="p-6 max-w-2xl space-y-6">
      <div>
        <h3 className="font-semibold mb-1">Identité de l'association</h3>
        <p className="text-sm text-muted-foreground">Ces informations apparaissent dans l'en-tête des PDF exportés (liste des mouvements, etc.).</p>
      </div>

      <div className="space-y-1.5">
        <Label>Nom de l'association</Label>
        <Input value={currentName} onChange={(e) => setName(e.target.value)} placeholder="Mon Association" />
      </div>

      <div className="space-y-2">
        <Label>Logo</Label>
        <div className="flex items-start gap-4">
          <div className="h-24 w-24 rounded-md border border-border bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <Building className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" disabled={uploading}>
                <label className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "Envoi…" : logoUrl ? "Remplacer" : "Téléverser"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </label>
              </Button>
              {logoUrl && (
                <Button variant="ghost" size="sm" onClick={removeLogo}><X className="h-4 w-4 mr-1" />Retirer</Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">PNG, JPG ou SVG. 2 Mo max. Format carré recommandé.</p>
          </div>
        </div>
      </div>

      <Button onClick={() => save.mutate({ name: currentName || null })} disabled={save.isPending}>Enregistrer</Button>
    </Card>
  );
}

const ITEM_STATES: { value: string; label: string; description: string }[] = [
  { value: "Processed", label: "Payé", description: "L'item du panier a été payé et traité avec succès par HelloAsso. Le paiement est encaissé et l'item est validé." },
  { value: "Registered", label: "Enregistré", description: "L'item est enregistré (billet, badge ou inscription confirmé(e))." },
  { value: "Waiting", label: "En attente", description: "Traitement en cours : le paiement ou la validation de l'item est en attente." },
  { value: "Refunded", label: "Remboursé", description: "L'item a été remboursé au payeur (total ou partiel)." },
  { value: "Refunding", label: "Remboursement en cours", description: "Une opération de remboursement est en cours de traitement par HelloAsso." },
  { value: "Refused", label: "Refusé", description: "Le paiement de l'item a été refusé (carte refusée, fonds insuffisants, etc.)." },
  { value: "Canceled", label: "Annulé", description: "L'item a été annulé avant traitement complet." },
  { value: "Abandoned", label: "Abandonné", description: "Le payeur a abandonné le processus de paiement avant validation." },
  { value: "Contested", label: "Contesté", description: "Le paiement fait l'objet d'une contestation (chargeback bancaire)." },
  { value: "Deleted", label: "Supprimé", description: "L'item a été supprimé." },
  { value: "Unknown", label: "Inconnu", description: "État inconnu — valeur de repli renvoyée par l'API HelloAsso lorsqu'aucun état défini ne s'applique." },
];

const PAYMENT_STATES: { value: string; description: string }[] = [
  { value: "Authorized", description: "Paiement autorisé par la banque du payeur — les fonds sont réservés et seront prélevés." },
  { value: "Pending", description: "Paiement en attente de traitement par HelloAsso ou la banque." },
  { value: "Registered", description: "Paiement enregistré et validé." },
  { value: "Refused", description: "Paiement refusé (carte refusée, fonds insuffisants, etc.)." },
  { value: "Refunded", description: "Paiement remboursé au payeur (total ou partiel)." },
  { value: "Refunding", description: "Remboursement en cours de traitement." },
  { value: "Contested", description: "Paiement contesté (chargeback bancaire initié par le payeur)." },
  { value: "Canceled", description: "Paiement annulé avant traitement." },
  { value: "WaitingBankValidation", description: "En attente de validation bancaire (prélèvement SEPA notamment)." },
  { value: "WaitingBankWithdraw", description: "En attente du prélèvement effectif sur le compte du payeur." },
  { value: "Unknown", description: "État inconnu — valeur de repli renvoyée par l'API HelloAsso." },
];

const CASH_OUT_STATES: { value: string; description: string }[] = [
  { value: "MoneyIn", description: "Portemonnaie HelloAsso plein — l'argent est reçu mais n'a pas encore été versé. Un virement doit être effectué depuis HelloAsso." },
  { value: "TransferInProgress", description: "Encaissement sur HelloAsso en cours — les fonds sont en cours de transfert vers le portemonnaie HelloAsso." },
  { value: "Transfered", description: "Encaissé sur HelloAsso — les fonds sont disponibles dans le portemonnaie HelloAsso." },
  { value: "WaitingForCashOutConfirmation", description: "Transfert des fonds HelloAsso vers le compte bancaire de l'association en cours de confirmation." },
  { value: "CashedOut", description: "Fonds HelloAsso transférés vers le compte bancaire de l'association." },
  { value: "Refunding", description: "Remboursement en cours — une opération de remboursement est en cours de traitement." },
  { value: "Refunded", description: "Remboursé — la commande a été remboursée au payeur." },
];

function HelloAssoDocsTab() {
  return (
    <div className="space-y-6 max-w-4xl">
      <Card className="p-4 border-primary/30 bg-primary/5">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            Cette page documente les différentes valeurs renvoyées par l'API HelloAsso et utilisées dans l'application
            (états des items du panier, états d'encaissement, etc.).
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-lg font-semibold mb-1">État d'un item de panier</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Visible dans le détail d'une commande (colonne « État » du panier). Chaque item peut avoir son propre état,
          indépendant du statut global de la commande.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Valeur API</TableHead>
              <TableHead className="w-[200px]">Affichage</TableHead>
              <TableHead>Signification</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ITEM_STATES.map((s) => (
              <TableRow key={s.value}>
                <TableCell><code className="text-xs">{s.value}</code></TableCell>
                <TableCell>{itemStateBadge(s.value)}</TableCell>
                <TableCell className="text-sm">{s.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-lg font-semibold mb-1">État d'un paiement</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Visible dans la liste des paiements du détail d'une commande (colonne « État »). Décrit l'état du paiement
          côté banque / opérateur.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Valeur API</TableHead>
              <TableHead className="w-[200px]">Affichage</TableHead>
              <TableHead>Signification</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {PAYMENT_STATES.map((s) => (
              <TableRow key={s.value}>
                <TableCell><code className="text-xs">{s.value}</code></TableCell>
                <TableCell>{paymentStateBadge(s.value)}</TableCell>
                <TableCell className="text-sm">{s.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-lg font-semibold mb-1">État d'encaissement d'une commande (cash_out_state)</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Visible dans la colonne « Paiement » des commandes et dans l'encart « Versement » du détail.
          Décrit le cycle de vie des fonds, depuis la réception jusqu'au virement bancaire.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[240px]">Valeur API</TableHead>
              <TableHead className="w-[200px]">Affichage</TableHead>
              <TableHead>Signification</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {CASH_OUT_STATES.map((s) => (
              <TableRow key={s.value}>
                <TableCell><code className="text-xs">{s.value}</code></TableCell>
                <TableCell>{cashOutBadge(s.value)}</TableCell>
                <TableCell className="text-sm">{s.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ─── Modèles d'emails système (invitation / réinitialisation) ──────────────
type SystemEmail = {
  key: "account_invitation" | "password_reset";
  label: string;
  subject: string;
  body_html: string;
  styles?: string;
  is_custom: boolean;
};

function SystemEmailsTab() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery<SystemEmail[]>({
    queryKey: ["system-emails"],
    queryFn: async () => {
      const r = await api.get<SystemEmail[]>("/api/system-emails");
      return Array.isArray(r) ? r : [];
    },
  });

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            Personnalisez le sujet et le corps HTML des emails envoyés automatiquement par
            l'application. Variables disponibles :{" "}
            <code className="px-1 rounded bg-muted">{"{{greeting}}"}</code>,{" "}
            <code className="px-1 rounded bg-muted">{"{{first_name}}"}</code>,{" "}
            <code className="px-1 rounded bg-muted">{"{{reset_url}}"}</code>,{" "}
            <code className="px-1 rounded bg-muted">{"{{email}}"}</code>. Le lien de
            réinitialisation est sécurisé par token (valable 24 h, usage unique).
          </p>
          <p className="text-xs">
            <strong>Mise en forme :</strong> la feuille de style CSS est stockée séparément
            sur le serveur (<code className="px-1 rounded bg-muted">api-php/helpers/system_email_styles/default.css</code>)
            et injectée automatiquement à l'envoi. Vous pouvez utiliser les classes{" "}
            <code className="px-1 rounded bg-muted">.email-container</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-btn</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-btn-wrap</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-fallback</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-fallback-url</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-hint</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-divider</code>,{" "}
            <code className="px-1 rounded bg-muted">.email-footer</code>.
          </p>
        </div>
      </Card>
      {isLoading && <Card className="p-6 text-sm text-muted-foreground">Chargement…</Card>}
      {data.map((tpl) => (
        <SystemEmailEditor key={tpl.key} template={tpl} onSaved={() => qc.invalidateQueries({ queryKey: ["system-emails"] })} />
      ))}
    </div>
  );
}

function SystemEmailEditor({ template, onSaved }: { template: SystemEmail; onSaved: () => void }) {
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.body_html);
  const [showPreview, setShowPreview] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      await api.put(`/api/system-emails/${template.key}`, { subject, body_html: bodyHtml });
    },
    onSuccess: () => {
      toast.success("Modèle enregistré");
      onSaved();
    },
    onError: (e: any) => toast.error(e?.message ?? "Erreur"),
  });

  const reset = useMutation({
    mutationFn: async () => {
      const r = await api.post<SystemEmail>(`/api/system-emails/${template.key}/reset`);
      return r;
    },
    onSuccess: (d) => {
      if (d) {
        setSubject(d.subject);
        setBodyHtml(d.body_html);
      }
      toast.success("Modèle réinitialisé par défaut");
      onSaved();
    },
    onError: (e: any) => toast.error(e?.message ?? "Erreur"),
  });

  const previewBody = bodyHtml
    .replaceAll("{{greeting}}", "Bonjour Marie,")
    .replaceAll("{{first_name}}", "Marie")
    .replaceAll("{{reset_url}}", "https://intranet.example.org/reset-password?token=…")
    .replaceAll("{{email}}", "marie@example.org");

  const previewDoc = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><style>${template.styles ?? ""}</style></head><body>${previewBody}</body></html>`;

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {template.label}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Clé : <code>{template.key}</code> ·{" "}
            {template.is_custom ? (
              <Badge variant="secondary">Personnalisé</Badge>
            ) : (
              <span className="italic">Modèle par défaut</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? "Masquer l'aperçu" : "Aperçu"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => reset.mutate()}
            disabled={reset.isPending || !template.is_custom}
            title="Restaurer le modèle par défaut"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Réinitialiser
          </Button>
          <Button type="button" size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            Enregistrer
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Sujet</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Corps de l'email</Label>
        <TiptapEditor value={bodyHtml} onChange={setBodyHtml} previewStyles={template.styles} />
      </div>

      {showPreview && (
        <div className="border rounded-md overflow-hidden">
          <div className="bg-muted px-3 py-1.5 text-xs font-medium border-b">
            Aperçu — Sujet : {subject}
          </div>
          <iframe
            title="Aperçu email"
            srcDoc={previewDoc}
            className="w-full bg-white"
            style={{ height: 480, border: 0 }}
          />
        </div>
      )}
    </Card>
  );
}



